import { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Award, GraduationCap, Clock, BookOpen } from 'lucide-react';
import { SplitWords, FadeSlideIn } from './AnimatedText';
import { TEST_IDS } from '@/constants/testIds';

const SPEAKING_IMAGE = 'https://customer-assets.emergentagent.com/job_f54f3a20-4df8-4151-89bb-3d96c747d48f/artifacts/ygyiw3w0_Sarah%2BWatson_AdAge_Awards_PAWELECphoto%2B-%2B72dpi%2B-%2B143.webp';

const CREDENTIALS = [
  { icon: Award, label: 'Former Chairman & Global CSO', detail: 'BBH New York' },
  { icon: GraduationCap, label: 'Oxford University', detail: 'BA English' },
  { icon: BookOpen, label: 'Four Advanced', detail: 'Coaching Certifications' },
  { icon: Clock, label: '25+ Years', detail: 'in Creative Agencies' },
];

const STATS = [
  { target: 25, suffix: '+', label: 'Years in Creative Leadership' },
  { target: 100, suffix: '+', label: 'Leaders Transformed' },
  { target: 6, suffix: '', label: 'Month Average Engagement' },
];

function CountUp({ target, suffix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

export default function AuthoritySection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      ref={sectionRef}
      id="authority"
      data-testid={TEST_IDS.authoritySection}
      className="bg-[#E8E0D5] py-24 md:py-32 px-6 md:px-12 relative overflow-hidden"
    >
      {/* Decorative */}

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image with Parallax */}
          <FadeSlideIn direction="left">
            <div className="relative img-zoom">
              <motion.div style={{ y: imgY }} className="overflow-hidden">
                <img
                  src={SPEAKING_IMAGE}
                  alt="Sarah Watson speaking at Ad Age Awards"
                  data-testid={TEST_IDS.authorityImage}
                  className="w-full h-[500px] md:h-[600px] object-cover object-center"
                  loading="lazy"
                />
              </motion.div>
              {/* Gold corner accent */}
              <motion.div
                className="absolute -bottom-3 -right-3 w-full h-full border-r-2 border-b-2 border-[#C9A96E]/20 -z-10"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.8 }}
              />
              <p className="text-xs uppercase tracking-[0.15em] text-[#2D2D2D]/40 font-sans mt-4">
                Speaking at Ad Age Small Agency Conference
              </p>
            </div>
          </FadeSlideIn>

          {/* Content */}
          <div>
            <FadeSlideIn direction="right">
              <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-[#C9A96E] font-sans font-semibold mb-6">
                <motion.span
                  initial={{ width: 0 }}
                  whileInView={{ width: 30 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="inline-block h-px bg-[#C9A96E] mr-3 align-middle"
                />
                Credentials
              </p>
              <h2
                data-testid={TEST_IDS.authorityHeadline}
                className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-[#1B2A4A] mb-10"
              >
                <SplitWords>25 Years at the Top. One Unmatched Perspective.</SplitWords>
              </h2>
            </FadeSlideIn>

            {/* Credential Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
              {CREDENTIALS.map((cred, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ x: 6 }}
                  className="flex items-start gap-4 group"
                >
                  <motion.div
                    className="w-10 h-10 flex items-center justify-center border border-[#C9A96E]/30 shrink-0 group-hover:border-[#C9A96E] group-hover:bg-[#C9A96E]/10 transition-all duration-300"
                    whileHover={{ rotate: 5 }}
                  >
                    <cred.icon className="w-5 h-5 text-[#C9A96E]" />
                  </motion.div>
                  <div>
                    <p className="font-sans font-medium text-[#1B2A4A] text-sm">{cred.label}</p>
                    <p className="font-sans text-[#2D2D2D]/60 text-sm">{cred.detail}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Stats with Counter Animation */}
            <div className="flex flex-wrap gap-8 pt-8 border-t border-[#1B2A4A]/10">
              {STATS.map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.5 + i * 0.12 }}
                >
                  <p className="font-serif text-4xl md:text-5xl text-[#1B2A4A] tracking-tight">
                    <CountUp target={stat.target} suffix={stat.suffix} />
                  </p>
                  <p className="text-xs uppercase tracking-[0.1em] text-[#2D2D2D]/40 font-sans mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
